import { ConfidenceSlider } from '@/components/ConfidenceSlider'
import { InfoTooltip } from '@/components/InfoTooltip'
import { fromPercent, toPercent } from '@/lib/confidence'
import type { AcceptanceDefaults } from '@/types/bundle'

export interface AcceptanceTabProps {
  acceptance: AcceptanceDefaults
  onChange: (acceptance: AcceptanceDefaults) => void
}

// The platform-wide floor every document type's own `defaults.acceptance`
// falls back to when it sets no override of its own (see Document Types'
// General tab) - this screen edits that floor directly, so there's no
// reference/ghost marker to show here, unlike the per-document-type sliders.
export function AcceptanceTab({ acceptance, onChange }: AcceptanceTabProps) {
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Acceptance defaults
          <InfoTooltip text="The platform-wide floor every document type falls back to when it sets no override of its own (Document Types > General tab)." />
        </h2>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Default min confidence
            <InfoTooltip text="The minimum confidence score a field needs before its value is accepted, unless a document type or the field itself sets its own override." />
          </span>
          <ConfidenceSlider
            value={toPercent(acceptance.min_confidence_default)}
            onChange={(percent) => onChange({ ...acceptance, min_confidence_default: fromPercent(percent) })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Default min confidence (critical fields)
            <InfoTooltip text="Same as above, but for fields marked Critical - critical fields need a higher confidence score before they're accepted." />
          </span>
          <ConfidenceSlider
            value={toPercent(acceptance.critical_min_confidence_default)}
            onChange={(percent) => onChange({ ...acceptance, critical_min_confidence_default: fromPercent(percent) })}
          />
        </div>
      </section>
    </div>
  )
}
